import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { isApiError } from '@/api/errors';
import { setUnauthorizedHandler } from '@/api/http';
import { sessionApi, type LoginInput, type Session } from '@/api/session';
import { clearRecent } from '@/app/recent';
import { setHomeCurrency } from '@/lib/money';
import { SessionContext, type SessionContextValue, type SessionState } from './session-context';
import { tokenStore, type Environment } from './token-store';

function remember(session: Session): void {
  if (!session.token) return;
  tokenStore.set({
    token: session.token,
    expiresAt: session.expires_at,
    tenantId: session.tenant.id,
    environment: session.environment,
  });
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<SessionState>(() =>
    tokenStore.get() ? { status: 'loading' } : { status: 'anonymous' },
  );
  const [switching, setSwitching] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const dropCaches = useCallback(async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
  }, [queryClient]);

  // Restore the tab's session (token in sessionStorage) on load.
  useEffect(() => {
    if (state.status !== 'loading') return;
    const controller = new AbortController();
    sessionApi.describe(controller.signal).then(
      (session) => {
        setState({ status: 'authenticated', session });
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        if (isApiError(error) && error.isUnauthenticated) {
          tokenStore.clear();
          setState({ status: 'anonymous', reason: 'expired' });
        } else {
          setState({ status: 'error', error });
        }
      },
    );
    return () => controller.abort();
  }, [state.status, attempt]);

  // Any 401 for the live credential ends the session.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      tokenStore.clear();
      queryClient.clear();
      setState({ status: 'anonymous', reason: 'expired' });
    });
    return () => setUnauthorizedHandler(null);
  }, [queryClient]);

  // Expire proactively when the token's lifetime ends.
  const expiresAt = state.status === 'authenticated' ? tokenStore.get()?.expiresAt : null;
  useEffect(() => {
    if (!expiresAt) return;
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (!Number.isFinite(ms)) return;
    const timer = setTimeout(
      () => {
        tokenStore.clear();
        queryClient.clear();
        setState({ status: 'anonymous', reason: 'expired' });
      },
      Math.max(ms, 0),
    );
    return () => clearTimeout(timer);
  }, [expiresAt, queryClient]);

  const login = useCallback(
    async (input: LoginInput) => {
      const session = await sessionApi.login(input);
      await dropCaches();
      remember(session);
      setState({ status: 'authenticated', session });
      return session;
    },
    [dropCaches],
  );

  const logout = useCallback(async () => {
    try {
      await sessionApi.logout();
    } catch {
      /* the token is discarded locally either way */
    }
    tokenStore.clear();
    clearRecent();
    await dropCaches();
    setState({ status: 'anonymous', reason: 'signed_out' });
  }, [dropCaches]);

  const switchContext = useCallback(
    async (target: { tenantId?: string; environment: Environment }) => {
      setSwitching(true);
      try {
        const session = await sessionApi.switch({
          tenant_id: target.tenantId,
          environment: target.environment,
        });
        await dropCaches();
        remember(session);
        setState({ status: 'authenticated', session });
        return session;
      } finally {
        setSwitching(false);
      }
    },
    [dropCaches],
  );

  const retry = useCallback(() => {
    setState({ status: 'loading' });
    setAttempt((n) => n + 1);
  }, []);

  // Money formatting follows the active tenant's home currency.
  setHomeCurrency(state.status === 'authenticated' ? state.session.tenant.default_currency : null);

  const value = useMemo<SessionContextValue>(
    () => ({ state, login, logout, switchContext, switching, retry }),
    [state, login, logout, switchContext, switching, retry],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}
